import { getAllowedEmbedderOrigins } from "./config";
import { ANALYTICS_EVENT_ALIASES } from "./types";
import type {
  AnalyticsDataLayerEvent,
  AnalyticsEcommercePayload,
  AnalyticsEventAlias,
  AnalyticsEventName,
  AnalyticsMessage,
  TrackingSource,
} from "./types";

/** Identifies our envelopes to embedders listening on `message`. */
export const ANALYTICS_MESSAGE_SOURCE = "collins-guest-checkout";

/** Envelope version, bumped if the payload contract ever changes shape. */
export const ANALYTICS_MESSAGE_VERSION = 1;

/**
 * Origin derived from the referring page, used only where no embedder origin is
 * configured for the environment.
 *
 * It is a weak signal: the referrer is empty under `Referrer-Policy:
 * no-referrer`, empty for a frame the embedder navigated programmatically, and
 * stale once the guest has navigated within the checkout. Configuration is
 * therefore the primary source — this remains as a fallback because an origin
 * parsed from the referrer is still an explicit origin, never `"*"`.
 */
function referrerOrigin(): string | null {
  const referrer = document.referrer;
  if (!referrer) return null;

  try {
    return new URL(referrer).origin;
  } catch {
    return null;
  }
}

/**
 * The origins an envelope may be posted to when the checkout runs in a frame.
 *
 * Every configured origin is a target in its own right: the browser delivers a
 * `postMessage` only when the target origin matches the receiving document's
 * own, so posting to each configured origin reaches the real embedder and no
 * one else, without the checkout having to know which of them framed it — which
 * is unknowable from inside a cross-origin frame.
 */
function resolveEmbedderOrigins(): readonly string[] {
  const configured = getAllowedEmbedderOrigins();
  if (configured.length > 0) return configured;

  const fallback = referrerOrigin();
  return fallback ? [fallback] : [];
}

/** The GTM-shaped half of an envelope, as pushed to `window.dataLayer`. */
export function toDataLayerEvent(message: AnalyticsMessage): AnalyticsDataLayerEvent {
  return { event: message.event, ecommerce: message.ecommerce };
}

/**
 * Builds the one envelope every destination uses.
 *
 * It carries Google's `dataLayer` keys (`event`, `ecommerce`) and our own
 * metadata at the same level, and the `dataLayer` push is derived from it by
 * {@link toDataLayerEvent}, so what a host page receives and what a container
 * receives cannot drift apart.
 *
 * A legacy event name, where the event has one, is carried as `dmn_event_alias`
 * for host-page consumers only: {@link toDataLayerEvent} does not project it, so
 * it never reaches GA4 as event data on an event Google already names.
 *
 * The journey cohort is lifted from the payload rather than read from the
 * constant again, so the envelope's marker and the ecommerce one cannot differ.
 */
export function buildAnalyticsMessage(
  event: AnalyticsEventName,
  trackingSource: TrackingSource,
  payload: AnalyticsEcommercePayload,
): AnalyticsMessage {
  const aliases: Partial<Record<AnalyticsEventName, AnalyticsEventAlias>> = ANALYTICS_EVENT_ALIASES;
  const alias = aliases[event];

  return {
    source: ANALYTICS_MESSAGE_SOURCE,
    version: ANALYTICS_MESSAGE_VERSION,
    event,
    ecommerce: payload,
    trackingSource,
    journey_version: payload.journey_version,
    payload,
    ...(alias ? { dmn_event_alias: alias } : {}),
  };
}

/**
 * Dual-fires an event to the host page via `window.postMessage`, alongside the
 * GTM/GA4 push, so embedders without Google Tag Manager — or with their own
 * analytics stack — can still consume the widget's events.
 *
 * Callers must gate this on the visitor's consent decision: a refusal, or no
 * decision yet, means no envelope is posted at all — neither to our own window
 * nor to the embedding page. See `docs/consent.md`. Delivery to the parent frame
 * only ever targets an explicit origin, never `"*"`. The envelope carries no card
 * data and no guest contact details.
 */
export function postAnalyticsMessage(message: AnalyticsMessage): void {
  try {
    window.postMessage(message, window.location.origin);

    if (window.parent && window.parent !== window) {
      for (const origin of resolveEmbedderOrigins()) {
        window.parent.postMessage(message, origin);
      }
    }
  } catch (error) {
    console.warn("[Analytics] Failed to post analytics message:", error);
  }
}
